import React, { useState, useRef } from 'react';
import { ADS_ENABLED } from '../config/admob';
import { isAndroidNative, showRewardedAd, claimAfterAd } from '../utils/admobManager';
import AndroidPromoModal from './AndroidPromoModal';

// `chance` must match the weights in spinWheelOfRiches on the server. Google requires the chances of a
// random reward to be shown before the video.
const PRIZES = [
  { id: 'points_100', label: '+100 Daily Pts', type: 'points', amount: 100, chance: 30, color: '#ff9800', icon: '🏆' },
  { id: 'stamina_25', label: '+25 Stamina', type: 'stamina', amount: 25, chance: 25, color: '#4caf50', icon: '🍖' },
  { id: 'points_250', label: '+250 Daily Pts', type: 'points', amount: 250, chance: 20, color: '#00e5ff', icon: '⚔️' },
  { id: 'xp_250', label: '+250 XP Scroll', type: 'xp', amount: 250, chance: 12, color: '#9c27b0', icon: '📜' },
  { id: 'points_500', label: '+500 Daily Pts', type: 'points', amount: 500, chance: 8, color: '#e91e63', icon: '💎' },
  { id: 'points_1000', label: '+1,000 Daily Pts', type: 'points', amount: 1000, chance: 5, color: '#ffd700', icon: '🐉' }
];

const WheelOfRichesModal = ({
  isOpen,
  onClose,
  playerData,
  spinWheelOfRichesCallable,
  fetchTokenBalances
}) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [wonPrize, setWonPrize] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [showPromo, setShowPromo] = useState(false);

  const isAndroid = isAndroidNative();
  const spinsUsed = playerData?.androidPerks?.wheelSpinsUsed || 0;
  const isFreeSpin = spinsUsed === 0;
  const adSpinsLeft = Math.max(0, 4 - Math.max(1, spinsUsed));
  const totalSpinsRemaining = Math.max(0, 4 - spinsUsed);

  if (!isOpen) return null;

  const handleSpinClick = async () => {
    if (isSpinning) return;

    if (!isAndroid) {
      setShowPromo(true);
      return;
    }

    if (totalSpinsRemaining <= 0) {
      setStatusMessage('⏳ Daily spin limit reached (4/4). Check back at midnight UTC!');
      return;
    }

    setStatusMessage('');
    setWonPrize(null);
    setIsSpinning(true);

    try {
      // 1. If it's an ad spin, watch the rewarded ad first
      if (!isFreeSpin) {
        if (!ADS_ENABLED) {
          setStatusMessage('Ad spins are temporarily disabled during testing.');
          setIsSpinning(false);
          return;
        }
        setStatusMessage('Loading video ad...');
        await showRewardedAd('wheelSpin');
        setStatusMessage('Spinning the wheel...');
      }

      // 2. Call backend to calculate the prize securely
      const result = await claimAfterAd(spinWheelOfRichesCallable, { isAdSpin: !isFreeSpin });

      if (result.data && result.data.success) {
        const { prizeIndex, prize } = result.data;

        // Calculate rotation degrees:
        // Needle is at 12 o'clock (0°).
        // Each of the 6 slices is 60°, with slice centers at index * 60 + 30.
        // To bring slice center to 12 o'clock, rotate clockwise by (360 - sliceCenter) % 360.
        const sliceAngle = 360 / PRIZES.length;
        const sliceCenter = prizeIndex * sliceAngle + sliceAngle / 2;
        const prizeAngle = (360 - sliceCenter) % 360;
        const currentMod = ((rotation % 360) + 360) % 360;
        let forwardDiff = prizeAngle - currentMod;
        if (forwardDiff <= 0) {
          forwardDiff += 360;
        }
        const fullSpins = 5 * 360; // 5 full rotations for dramatic effect
        const targetRotation = rotation + fullSpins + forwardDiff;

        setRotation(targetRotation);

        // Wait for CSS transition (4 seconds)
        setTimeout(() => {
          setIsSpinning(false);
          setWonPrize(prize);
          setStatusMessage(`🎉 Congratulations! You won ${prize.label}!`);
          if (fetchTokenBalances) fetchTokenBalances();
        }, 4000);
      } else {
        throw new Error(result.data?.message || 'Server failed to calculate prize.');
      }
    } catch (err) {
      console.error('[WheelOfRiches] Error spinning wheel:', err);
      setIsSpinning(false);
      setStatusMessage(`❌ ${err.message || 'Error occurred during spin.'}`);
    }
  };

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.88)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}>
        <div style={{
          background: 'radial-gradient(circle at top, #241d38, #0e0d16)',
          borderRadius: '20px',
          border: '2px solid #ffd700',
          boxShadow: '0 0 40px rgba(255, 215, 0, 0.35)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          color: '#fff',
          padding: '24px',
          position: 'relative',
          textAlign: 'center'
        }}>
          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isSpinning}
            style={{
              position: 'absolute',
              top: '12px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: '#aaa',
              fontSize: '26px',
              cursor: isSpinning ? 'not-allowed' : 'pointer'
            }}
          >
            &times;
          </button>

          {/* Title */}
          <h2 style={{
            margin: '0 0 4px',
            color: '#ffd700',
            fontSize: '1.8em',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
          }}>
            🎰 Wheel of Riches
          </h2>
          <p style={{ margin: '0 0 20px', color: '#aaa', fontSize: '0.9em' }}>
            {isFreeSpin
              ? '🎁 Free Daily Spin Available!'
              : totalSpinsRemaining > 0
                ? `📺 Watch Ads to Spin (${adSpinsLeft}/3 Ad Spins Left Today)`
                : '✅ All 4 daily spins completed. Resets tomorrow!'}
          </p>

          {/* Wheel Container with Pointer */}
          <div style={{ position: 'relative', width: '280px', height: '280px', margin: '0 auto 20px' }}>
            {/* Pointer / Flapper */}
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '14px solid transparent',
              borderRight: '14px solid transparent',
              borderTop: '24px solid #ff1744',
              zIndex: 10,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))'
            }} />

            {/* The Rotating Wheel */}
            <div style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '6px solid #ffd700',
              boxShadow: '0 0 25px rgba(255, 215, 0, 0.5), inset 0 0 20px rgba(0,0,0,0.8)',
              position: 'relative',
              overflow: 'hidden',
              transform: `rotate(${rotation}deg)`,
              transition: isSpinning ? 'transform 4s cubic-bezier(0.15, 0.85, 0.35, 1)' : 'none',
              background: `conic-gradient(
                #ff9800 0deg 60deg,
                #4caf50 60deg 120deg,
                #00e5ff 120deg 180deg,
                #9c27b0 180deg 240deg,
                #e91e63 240deg 300deg,
                #ffd700 300deg 360deg
              )`
            }}>
              {/* Wheel Slice Labels */}
              {PRIZES.map((prize, index) => {
                const angle = index * 60 + 30; // Center of 60deg slice
                const isLeftHalf = angle > 180;
                return (
                  <div
                    key={prize.id}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: '120px',
                      height: '30px',
                      marginLeft: '-60px',
                      marginTop: '-15px',
                      transform: `rotate(${angle - 90}deg) translate(80px) ${isLeftHalf ? 'rotate(180deg)' : ''}`,
                      transformOrigin: 'center center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: index === 5 ? '#000' : '#fff',
                      fontWeight: 'bold',
                      fontSize: '11px',
                      textShadow: index === 5 ? 'none' : '0 1px 3px rgba(0,0,0,0.9)',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none'
                    }}
                  >
                    <span>{prize.icon} {prize.label}</span>
                  </div>
                );
              })}

              {/* Center Hub */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '54px',
                height: '54px',
                marginLeft: '-27px',
                marginTop: '-27px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, #ffd700, #b29400)',
                border: '3px solid #fff',
                boxShadow: '0 0 10px rgba(0,0,0,0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                💎
              </div>
            </div>
          </div>

          {/* Winner Banner */}
          {wonPrize && (
            <div style={{
              background: 'linear-gradient(90deg, rgba(255,215,0,0.2), rgba(255,215,0,0.4), rgba(255,215,0,0.2))',
              border: '1px solid #ffd700',
              borderRadius: '8px',
              padding: '10px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#ffd700' }}>
                🎉 {wonPrize.icon} Won: {wonPrize.label}!
              </div>
            </div>
          )}

          {/* Status Message */}
          {statusMessage && !wonPrize && (
            <p style={{ fontSize: '0.9em', color: '#00e5ff', margin: '0 0 12px' }}>
              {statusMessage}
            </p>
          )}

          {/* Prizes and chances, shown before any spin or video */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '10px',
            padding: '10px 12px',
            marginBottom: '14px',
            fontSize: '0.8em',
            textAlign: 'left'
          }}>
            <strong style={{ color: '#ffd700' }}>Prizes and chances per spin</strong>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', marginTop: '6px', color: '#ddd' }}>
              {PRIZES.map((prize) => (
                <span key={prize.id}>{prize.label}: {prize.chance}%</span>
              ))}
            </div>
            <div style={{ color: '#aaa', marginTop: '6px' }}>
              Daily Pts from video spins count toward your daily score, not your Store balance.
            </div>
          </div>

          {/* Action Button */}
          {isAndroid ? (
            <button
              onClick={handleSpinClick}
              disabled={isSpinning || totalSpinsRemaining <= 0 || (!isFreeSpin && !ADS_ENABLED)}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '1.15em',
                fontWeight: 'bold',
                borderRadius: '10px',
                border: 'none',
                cursor: (isSpinning || totalSpinsRemaining <= 0 || (!isFreeSpin && !ADS_ENABLED)) ? 'not-allowed' : 'pointer',
                background: totalSpinsRemaining > 0 && (isFreeSpin || ADS_ENABLED)
                  ? (isFreeSpin
                    ? 'linear-gradient(135deg, #4caf50, #2e7d32)'
                    : 'linear-gradient(135deg, #ffd700, #ff8f00)')
                  : '#444',
                color: (totalSpinsRemaining > 0 && !isFreeSpin && ADS_ENABLED) ? '#000' : '#fff',
                boxShadow: (totalSpinsRemaining > 0 && (isFreeSpin || ADS_ENABLED)) ? '0 0 20px rgba(255, 215, 0, 0.4)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              {isSpinning
                ? 'Spinning...'
                : isFreeSpin
                  ? '🎁 Free Daily Spin!'
                  : !ADS_ENABLED
                    ? '📺 Ad Spins Disabled During Testing'
                    : totalSpinsRemaining > 0
                      ? `📺 Watch Ad to Spin (${adSpinsLeft} Left)`
                      : '⏳ Limit Reached (4/4)'}
            </button>
          ) : (
            <button
              onClick={() => setShowPromo(true)}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '1.1em',
                fontWeight: 'bold',
                borderRadius: '10px',
                border: '1px dashed #00e5ff',
                background: 'rgba(0, 229, 255, 0.1)',
                color: '#00e5ff',
                cursor: 'pointer'
              }}
            >
              📱 Available in the Android App
            </button>
          )}
        </div>
      </div>

      <AndroidPromoModal
        isOpen={showPromo}
        onClose={() => setShowPromo(false)}
        perkName="The Wheel of Riches"
      />
    </>
  );
};

export default WheelOfRichesModal;
